/**
 * Centralized IVA calculation utilities
 * Ensures consistent IVA calculation across the entire application
 */

export interface IvaCalculationParams {
  subtotal: number;
  discount: number;
  deliveryFee: number;
  ivaRate: number;
  enabled: boolean;
}

export interface IvaCalculationResult {
  ivaAmount: number;
  totalWithIva: number;
  baseForIva: number;
}

/**
 * Calculate IVA amount using the correct formula: (subtotal - discount + delivery_fee) * iva_rate
 */
export function calculateIvaAmount(params: IvaCalculationParams): number {
  const { subtotal, discount, deliveryFee, ivaRate, enabled } = params;
  
  if (!enabled) return 0;
  
  const baseForIva = subtotal - discount + deliveryFee;
  return baseForIva * ivaRate;
}

/**
 * Calculate complete IVA breakdown
 */
export function calculateIvaBreakdown(params: IvaCalculationParams): IvaCalculationResult {
  const { subtotal, discount, deliveryFee, ivaRate, enabled } = params;
  
  const baseForIva = subtotal - discount + deliveryFee;
  const ivaAmount = enabled ? baseForIva * ivaRate : 0;
  const totalWithIva = baseForIva + ivaAmount;
  
  return {
    ivaAmount,
    totalWithIva,
    baseForIva
  };
}

/**
 * Calculate order total with other taxes included
 */
export function calculateOrderTotal(
  params: IvaCalculationParams,
  otherTaxes: number = 0
): number {
  const { totalWithIva } = calculateIvaBreakdown(params);
  return totalWithIva + otherTaxes;
}
